import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useState, useMemo, useEffect } from 'react';
import { GridRow, useGridApiContext, useGridSelector, gridFilteredTopLevelRowCountSelector, GridColumnMenu, gridFilteredDescendantCountLookupSelector } from '@mui/x-data-grid-premium';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { Button, Flex, EmptyState, AvatarGroup, Avatar, Body, Overline, OverflowTooltip, useConfigContextLoose, TagChipGroup, TagChip, CounterChip, StatusChip, Input, DatePicker, DateTimePicker, Tooltip } from '@cognite/cogs-core';
import { TableViewSmallIcon, TableViewMediumIcon, TableViewLargeIcon, FilterFilledIcon, ReorderDescendingIcon, ReorderDefaultIcon, ReorderAscendingIcon, FindIllustration, ExternalLinkIcon, FilterIcon, TreeIcon, SortAscendingIcon, EyeHideIcon, ChevronDownIcon, ChevronRightIcon } from '@cognite/cogs-icons';
import { IconWrapper } from '@cognite/cogs-utils';
import { Dropdown } from '../dropdown/dropdown.js';
import { Menu } from '../menu/menu.js';
import { FilterForm, GroupByForm, SortForm, ReorderableCheckList } from '../page-toolbar/elements.js';
import '@mui/base';
import '@mui/base/useList';
import '@mui/utils';
import { Select } from '../select/select.js';
import { useDataGridContext } from './datagrid-context.js';
import { MultiSelectFilter } from './datagrid-filters.js';
import { useDataGridHeaderCell } from './hooks.js';
import { SkeletonRow } from './skeleton-row.js';
import { getSelectValueOptions, getTableRowHeightVariant } from './utils.js';

var densityIconMapper = {
  compact: jsx(TableViewSmallIcon, {}),
  standard: jsx(TableViewMediumIcon, {}),
  comfortable: jsx(TableViewLargeIcon, {})
};
var DataGridColumnFilteredIcon = function DataGridColumnFilteredIcon() {
  return jsx(Button, {
    icon: jsx(FilterFilledIcon, {}),
    type: "ghost",
    "aria-label": "Filter",
    size: "small"
  });
};
var DataGridColumnSortedDescendingIcon = function DataGridColumnSortedDescendingIcon() {
  return jsx(Button, {
    icon: jsx(ReorderDescendingIcon, {}),
    type: "ghost",
    "aria-label": "Descending sort",
    size: "small"
  });
};
var DataGridColumnReorderIcon = function DataGridColumnReorderIcon() {
  return jsx(Button, {
    icon: jsx(ReorderDefaultIcon, {}),
    type: "ghost",
    "aria-label": "No sorting",
    size: "small"
  });
};
var DataGridColumnSortedAscendingIcon = function DataGridColumnSortedAscendingIcon() {
  return jsx(Button, {
    icon: jsx(ReorderAscendingIcon, {}),
    type: "ghost",
    "aria-label": "Ascending sort",
    size: "small"
  });
};
var DataGridRow = function DataGridRow(params) {
  var _a;
  if ((_a = params.row) === null || _a === undefined ? undefined : _a.cogsAutomaticGeneratedSkeletonRow) {
    return jsx(SkeletonRow, Object.assign({}, params));
  }
  return jsx(GridRow, Object.assign({}, params));
};
var DataGridNoResult = function DataGridNoResult() {
  var _useDataGridContext = useDataGridContext(),
    localeText = _useDataGridContext.localeText;
  var localTextNoResults = localeText === null || localeText === undefined ? undefined : localeText.noResultsFound;
  return jsx(Flex, {
    direction: "column",
    justifyContent: "center",
    alignItems: "center",
    className: "cogs-datagrid__overlay",
    children: jsx(EmptyState, {
      title: (localTextNoResults === null || localTextNoResults === undefined ? undefined : localTextNoResults.title) || 'No results',
      illustration: jsx(FindIllustration, {}),
      description: (localTextNoResults === null || localTextNoResults === undefined ? undefined : localTextNoResults.description) || 'Try adjusting your search terms and filters'
    })
  });
};
var DataGridNoRows = function DataGridNoRows() {
  var _useDataGridContext2 = useDataGridContext(),
    localeText = _useDataGridContext2.localeText;
  var localTextNoRows = localeText === null || localeText === undefined ? undefined : localeText.noRowsFound;
  return jsx(Flex, {
    direction: "column",
    justifyContent: "center",
    alignItems: "center",
    className: "cogs-datagrid__overlay",
    children: jsx(EmptyState, {
      title: (localTextNoRows === null || localTextNoRows === undefined ? undefined : localTextNoRows.title) || 'No data available',
      illustration: jsx(FindIllustration, {}),
      description: (localTextNoRows === null || localTextNoRows === undefined ? undefined : localTextNoRows.description) || 'Start by adding some data to see it populate in this table'
    })
  });
};
var DataGridAvatarCell = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var avatar = _ref.avatar,
    showName = _ref.showName,
    avatarGroupProps = _ref.avatarGroupProps;
  var _a;
  return jsxs(Flex, {
    alignItems: "center",
    gap: 4,
    ref: ref,
    children: [Array.isArray(avatar) ? jsx(AvatarGroup, Object.assign({
      appendTo: (_a = document.querySelector('.cogs-datagrid')) !== null && _a !== undefined ? _a : undefined,
      size: "small",
      overflow: 2
    }, avatarGroupProps, {
      children: avatar.map(function (ava) {
        return jsx(Avatar, Object.assign({
          tooltipProps: {
            // Have to use appendTo to avoid the tooltip being mounted parent as cell as in this case the parent should always be main datagrid component
            appendTo: document.querySelector('.cogs-datagrid')
          }
        }, ava));
      })
    })) : jsx(Avatar, Object.assign({
      size: "small",
      tooltipProps: {
        appendTo: document.querySelector('.cogs-datagrid')
      }
    }, avatar)), showName && !Array.isArray(avatar) && jsx(Body, {
      size: "medium",
      children: avatar.text
    })]
  });
});
var DataGridRowStringCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var title = props.title,
    subtitle = props.subtitle,
    icon = props.icon,
    prefix = props.prefix,
    suffix = props.suffix,
    overline = props.overline,
    rest = __rest(props, ["title", "subtitle", "icon", "prefix", "suffix", "overline"]);
  return jsxs(Flex, Object.assign({
    alignItems: "center",
    gap: 8,
    className: "cogs-lab cogs-datagrid-cell cogs-datagrid-stringcell"
  }, rest, {
    children: [icon && jsx(Flex, {
      flex: 1,
      children: jsx(IconWrapper, {
        size: 16,
        children: icon
      })
    }), jsxs(Flex, {
      className: icon ? 'cogs-datagrid-stringcell-with-icon' : 'cogs-datagrid-stringcell',
      direction: "column",
      alignItems: "start",
      children: [overline && jsx(Overline, {
        size: "small",
        muted: true,
        children: overline
      }), jsxs(Flex, {
        alignItems: "center",
        gap: 4,
        className: "cogs-datagrid-stringcell",
        children: [prefix && jsx(Body, {
          size: "small",
          muted: true,
          children: prefix
        }), title && jsx(Body, {
          size: "medium",
          className: "cogs-body--align-left",
          noOfLines: 1,
          children: jsxs(OverflowTooltip, {
            children: [title, " "]
          })
        }), suffix && jsx(Body, {
          size: "small",
          muted: true,
          children: suffix
        })]
      }), subtitle && jsx(Body, {
        size: "x-small",
        className: " cogs-body--align-left",
        muted: true,
        noOfLines: 1,
        children: jsx(OverflowTooltip, {
          children: subtitle
        })
      })]
    })]
  }));
});
var DataGridNumberCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var value = props.value,
    icon = props.icon,
    prefix = props.prefix,
    formatValue = props.formatValue,
    suffix = props.suffix,
    rest = __rest(props, ["value", "icon", "prefix", "formatValue", "suffix"]);
  var formattedValue = formatValue ? formatValue(value) : new Intl.NumberFormat().format(value);
  return jsxs(Flex, Object.assign({
    ref: ref,
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    className: "cogs-lab cogs-datagrid-cell"
  }, rest, {
    children: [icon, jsxs(Flex, {
      alignItems: "center",
      gap: 4,
      children: [prefix && jsx(Body, {
        size: "x-small",
        muted: true,
        children: prefix
      }), jsx(Body, {
        size: "medium",
        children: formattedValue
      }), suffix && jsx(Body, {
        size: "x-small",
        muted: true,
        children: suffix
      })]
    })]
  }));
});
var DataGridLinkCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var gridCellProps = props.gridCellProps,
    target = props.target,
    children = props.children,
    className = props.className,
    rest = __rest(props, ["gridCellProps", "target", "children", "className"]);
  return jsx("div", Object.assign({}, gridCellProps, {
    children: jsxs("a", Object.assign({
      className: classnames('cogs-lab', 'cogs-link', 'cogs-datagrid-cell--link', className)
    }, rest, {
      target: target,
      ref: ref,
      children: [jsx("span", {
        className: "cogs-lab cogs-datagrid-link__text cogs-body--ellipsis",
        children: children
      }), target === '_blank' && jsx(ExternalLinkIcon, {
        width: 12,
        height: 12
      })]
    }))
  }));
});
var DataGridChipCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _a;
  var chip = props.chip,
    chipGroupProps = props.chipGroupProps,
    type = props.type;
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  return jsxs(Flex, {
    alignItems: "center",
    children: [type === 'tag' && Array.isArray(chip) && jsx(TagChipGroup, Object.assign({
      overflow: 3,
      size: "x-small",
      appendTo: (_a = getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer()) !== null && _a !== undefined ? _a : document.body
    }, chipGroupProps, {
      chips: chip
    })), type === 'tag' && !Array.isArray(chip) && jsx(TagChip, Object.assign({}, chip)), type === 'counter' && jsx(CounterChip, Object.assign({}, chip)), type === 'status' && jsx(StatusChip, Object.assign({}, chip))]
  });
});
function DataGridToolbar(_ref2) {
  var hideFilters = _ref2.hideFilters,
    hideGroupBy = _ref2.hideGroupBy,
    hideSortBy = _ref2.hideSortBy,
    hideDensity = _ref2.hideDensity,
    hideReorder = _ref2.hideReorder;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showFilters = _useState2[0],
    setShowFilters = _useState2[1];
  var _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    groupByItems = _useState4[0],
    setGroupByItems = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showGroupBy = _useState6[0],
    setShowGroupBy = _useState6[1];
  var apiRef = useGridApiContext();
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    sortByItems = _useState8[0],
    setSortByItems = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    showSortBy = _useState10[0],
    setShowSortBy = _useState10[1];
  var _React$useState = React.useState('standard'),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    density = _React$useState2[0],
    setDensity = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    showDensity = _React$useState4[0],
    setShowDensity = _React$useState4[1];
  var rowsCount = useGridSelector(apiRef, gridFilteredTopLevelRowCountSelector);
  var densitiesOptions = ['compact', 'standard', 'comfortable'];
  var densitiesLabel = {
    compact: 'Small',
    standard: 'Medium',
    comfortable: 'Large'
  };
  var columns = apiRef.current.getAllColumns();
  var _useState11 = useState(columns.map(function (item) {
      return Object.assign(Object.assign({}, item), {
        checked: true
      });
    })),
    _useState12 = _slicedToArray(_useState11, 2),
    reorderItems = _useState12[0],
    setReorderItems = _useState12[1];
  var filteredColumns = columns.filter(function (column) {
    return column.filterable;
  }).map(function (item) {
    return {
      type: item.type || 'string',
      value: item.field,
      label: item.headerName
    };
  });
  var filterModel = apiRef.current.state.filter.filterModel;
  var groupableColumns = columns.filter(function (column) {
    return column.groupable;
  }).map(function (item) {
    return {
      type: item.type || 'string',
      value: item.field,
      label: item.headerName
    };
  });
  var nonEmptyFilterItems = useMemo(function () {
    return filterModel.items.filter(function (filter) {
      return filter.value !== null && filter.value !== '';
    }).map(function (filter) {
      return {
        field: filter.field,
        operator: filter.operator || '',
        value: filter.value
      };
    });
  }, [filterModel.items]);
  var handleColumnVisibleChange = function handleColumnVisibleChange(currentItem, visible) {
    var newItems = reorderItems.map(function (item) {
      if (item.field === currentItem) {
        apiRef.current.setColumnVisibility(currentItem, visible);
        return Object.assign(Object.assign({}, item), {
          checked: visible
        });
      }
      return item;
    });
    setReorderItems(newItems);
  };
  var validGroupItems = useMemo(function () {
    return groupByItems.filter(function (item) {
      return item.value !== null && item.value !== '';
    }).map(function (item) {
      return item.value;
    });
  }, [groupByItems]);
  var validSortItems = useMemo(function () {
    return sortByItems.filter(function (item) {
      return item.sort;
    });
  }, [sortByItems]);
  useEffect(function () {
    apiRef.current.setRowGroupingModel(validGroupItems);
  }, [apiRef, validGroupItems]);
  useEffect(function () {
    apiRef.current.setSortModel(validSortItems);
  }, [apiRef, validSortItems]);
  var handleReorder = function handleReorder(newValues) {
    setReorderItems(newValues);
    newValues.forEach(function (column, index) {
      apiRef.current.setColumnIndex(column.field, index);
    });
  };
  useEffect(function () {
    apiRef.current.setDensity(density);
  }, [apiRef, density]);
  var counterChipProps = nonEmptyFilterItems.length > 0 ? {
    counterChip: {
      counter: nonEmptyFilterItems.length
    }
  } : {};
  var filterButtonToggled = showFilters || nonEmptyFilterItems.length > 0;
  var groupButtonToggled = showGroupBy || validGroupItems.length > 0;
  var groupCounterChipProps = validGroupItems.length > 0 ? {
    counterChip: {
      counter: validGroupItems.length
    }
  } : {};
  var sortButtonToggled = showSortBy || validSortItems.length > 0;
  var sortCounterChipProps = validSortItems.length > 0 ? {
    counterChip: {
      counter: validSortItems.length
    }
  } : {};
  var defaultDensity = 'standard';
  return jsxs(Flex, {
    gap: 8,
    "data-testid": "cogs-datagrid-toolbar",
    className: "cogs-datagrid__toolbar",
    children: [!hideFilters && jsx(Dropdown, {
      offset: {
        mainAxis: 4
      },
      onShow: function onShow() {
        return setShowFilters(true);
      },
      onHide: function onHide() {
        return setShowFilters(false);
      },
      content: jsx(FilterForm, {
        columns: filteredColumns,
        filterModel: filterModel,
        onFilterModelChange: apiRef.current.setFilterModel,
        resultText: "Results: ".concat(rowsCount)
      }),
      visible: showFilters,
      children: jsx(Button, Object.assign({
        type: "ghost",
        size: "small",
        toggled: filterButtonToggled
      }, counterChipProps, {
        icon: filterButtonToggled ? jsx(FilterFilledIcon, {}) : jsx(FilterIcon, {}),
        children: nonEmptyFilterItems.length > 0 ? 'Filtered' : 'Filters'
      }))
    }), !hideGroupBy && jsx(Dropdown, {
      offset: {
        mainAxis: 4
      },
      visible: showGroupBy,
      onShow: function onShow() {
        return setShowGroupBy(true);
      },
      onHide: function onHide() {
        return setShowGroupBy(false);
      },
      content: jsx(GroupByForm, {
        columns: groupableColumns,
        items: groupByItems,
        onItemChange: setGroupByItems
      }),
      children: jsx(Button, Object.assign({
        type: "ghost",
        size: "small",
        toggled: groupButtonToggled
      }, groupCounterChipProps, {
        icon: jsx(TreeIcon, {}),
        children: validGroupItems.length > 0 ? 'Grouped' : 'Group by'
      }))
    }), !hideSortBy && jsx(Dropdown, {
      visible: showSortBy,
      onShow: function onShow() {
        return setShowSortBy(true);
      },
      onHide: function onHide() {
        return setShowSortBy(false);
      },
      content: jsx(SortForm, {
        columns: filteredColumns,
        items: sortByItems,
        onItemChange: setSortByItems
      }),
      children: jsx(Button, Object.assign({
        size: "small",
        type: "ghost",
        toggled: sortButtonToggled
      }, sortCounterChipProps, {
        icon: jsx(SortAscendingIcon, {}),
        children: validSortItems.length > 0 ? 'Sorted' : 'Sort by'
      }))
    }), !hideDensity && jsx(Menu, {
      open: showDensity,
      onOpenChange: function onOpenChange(open) {
        return setShowDensity(open);
      },
      renderTrigger: function renderTrigger(props) {
        return jsx(Button, Object.assign({}, props, {
          size: "small",
          type: "ghost",
          toggled: showDensity,
          icon: densityIconMapper[density],
          children: "Density"
        }));
      },
      children: densitiesOptions.map(function (densityOption) {
        return jsx(Menu.ItemToggled, {
          toggled: density === densityOption,
          onClick: function onClick() {
            return setDensity(densityOption);
          },
          label: "".concat(densitiesLabel[densityOption], " ").concat(defaultDensity === densityOption ? '(default)' : '')
        }, densityOption);
      })
    }), !hideReorder && jsx(ReorderableCheckList, {
      searchable: true,
      reorderable: true,
      title: "Manage columns",
      description: "Hide columns and re-order",
      onColumnVisibleChange: handleColumnVisibleChange,
      triggerLabel: "Manage column",
      onSelectAllChange: function onSelectAllChange(items) {
        return apiRef.current.setColumnVisibilityModel(items);
      },
      items: reorderItems,
      onItemChange: handleReorder,
      icon: jsx(EyeHideIcon, {})
    })]
  });
}
var DataGridEditableCell = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var type = props.colDef.type;
  var api = props.api,
    value = props.value,
    id = props.id,
    field = props.field,
    colDef = props.colDef;
  var height = api.unstable_getRowHeight(id);
  var handleChange = function handleChange(event) {
    api.setEditCellValue({
      id: id,
      field: field,
      value: event.target.value
    });
  };
  // TODO add generic type for the use case of DatePicker and DateTimePicker
  var handleDateChange = function handleDateChange(date) {
    api.setEditCellValue({
      id: id,
      field: field,
      value: date === null || date === undefined ? undefined : date.toDate()
    });
  };
  var variant = getTableRowHeightVariant(height);
  if (type === 'string') {
    return jsx(Input, {
      "data-testid": "cogs-datagrid-editable-cell-string",
      size: variant,
      fullWidth: true,
      ref: ref,
      value: value,
      onChange: handleChange
    });
  }
  if (type === 'number') {
    return jsx(Input, {
      "data-testid": "cogs-datagrid-editable-cell-number",
      size: variant,
      type: "number",
      fullWidth: true,
      ref: ref,
      value: value,
      onChange: handleChange
    });
  }
  if (colDef.type === 'date') {
    return jsx(DatePicker, {
      "data-testid": "cogs-datagrid-editable-cell-datepicker",
      size: variant,
      value: dayjs(value),
      onChange: handleDateChange,
      fullWidth: true,
      ref: ref
    });
  }
  if (colDef.type === 'dateTime') {
    return jsx(DateTimePicker, {
      "data-testid": "cogs-datagrid-editable-cell-datetimepicker",
      size: variant,
      value: dayjs(value),
      onChange: handleDateChange,
      fullWidth: true,
      ref: ref
    });
  }
  if (colDef.type === 'singleSelect') {
    var locColDef = colDef;
    var getOptionValue = locColDef.getOptionValue;
    var getOptionLabel = locColDef.getOptionLabel;
    var valueOptions = getSelectValueOptions(locColDef, {
      id: id,
      row: props.row
    });
    if (!valueOptions) return null;
    return jsx(Select, {
      "data-testid": "cogs-datagrid-editable-cell-select",
      size: variant,
      fullWidth: true,
      options: valueOptions.map(function (option) {
        return {
          label: getOptionLabel(option),
          value: getOptionValue(option)
        };
      }),
      value: value,
      onChange: function onChange(_, value) {
        api.setEditCellValue({
          id: id,
          field: field,
          value: value
        });
      }
    });
  }
  return null;
});
var DataGridHeaderCell = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var colDef = _a.colDef,
    filterModel = _a.filterModel,
    children = _a.children,
    onFilterModelChange = _a.onFilterModelChange,
    localeText = _a.localeText,
    filterTrigger = _a.filterTrigger,
    portalTarget = _a.portalTarget,
    className = _a.className,
    rest = __rest(_a, ["colDef", "filterModel", "children", "onFilterModelChange", "localeText", "filterTrigger", "portalTarget", "className"]);
  var filter = colDef.filter;
  var _useDataGridHeaderCel = useDataGridHeaderCell({
      colDef: colDef,
      filterModel: filterModel,
      onFilterModelChange: onFilterModelChange
    }),
    handleOnFilter = _useDataGridHeaderCel.handleOnFilter,
    columnfilterModel = _useDataGridHeaderCel.columnfilterModel;
  var getFilter = function getFilter() {
    if (filter) {
      return jsx(MultiSelectFilter, {
        filterTrigger: filterTrigger,
        filter: filter,
        columnFilterState: columnfilterModel,
        onColumnFilterChange: handleOnFilter,
        multiSelectFilterLocaleText: localeText === null || localeText === undefined ? undefined : localeText.multiSelectFilterLocaleText,
        portalTarget: portalTarget
      });
    }
    return null;
  };
  return jsxs(Flex, Object.assign({
    alignItems: "center",
    gap: 8,
    className: classnames('cogs-datagrid--header-cell', className),
    ref: ref
  }, rest, {
    children: [jsx(Body, {
      noOfLines: 1,
      size: "medium",
      strong: true,
      className: "cogs-datagrid--header-cell__content cogs-body--align-left",
      children: jsx(OverflowTooltip, {
        children: colDef.headerName || ''
      })
    }), getFilter(), children]
  }));
});
var DataGridColumnMenu = function DataGridColumnMenu(props) {
  return jsx(GridColumnMenu, Object.assign({
    className: "cogs-lab cogs-lab-menu"
  }, props, {
    slots: Object.assign({
      columnMenuPinningItem: null,
      columnMenuGroupingItem: null,
      columnMenuAggregationItem: null
    }, props.slots)
  }));
};
/**
 * The DataGridTreeCell component is a custom cell renderer that displays a tree structure in a cell.
 * @param props - The props of the DataGridTreeCell component.
 *
 */
var DataGridTreeCell = function DataGridTreeCell(props) {
  var _a;
  var id = props.id,
    field = props.field,
    rowNode = props.rowNode,
    value = props.value;
  var apiRef = useGridApiContext();
  var ref = React.useRef(null);
  var filteredDescendantCountLookup = useGridSelector(apiRef, gridFilteredDescendantCountLookupSelector);
  var filteredDescendantCount = (_a = filteredDescendantCountLookup[rowNode.id]) !== null && _a !== undefined ? _a : 0;
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isOverflowing = _useState14[0],
    setIsOverflowing = _useState14[1];
  useEffect(function () {
    var element = ref.current;
    var checkOverflow = function checkOverflow() {
      if (element) {
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };
    checkOverflow();
    var resizeObserver = apiRef.current.subscribeEvent('columnResizeStop', checkOverflow);
    return function () {
      resizeObserver();
    };
  }, [apiRef]);
  var handleClick = function handleClick(event) {
    if (rowNode.type !== 'group') {
      return;
    }
    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };
  var marginLeft = rowNode.depth * 28;
  return jsxs(Flex, {
    gap: 4,
    className: "cogs-lab cogs-datagrid-tree-cell-container",
    alignItems: "center",
    style: {
      marginLeft: marginLeft,
      width: "calc(100% - ".concat(marginLeft, "px)")
    },
    children: [filteredDescendantCount > 0 && rowNode.type === 'group' ? jsx(Button, {
      size: "small",
      type: "ghost",
      onClick: handleClick,
      icon: rowNode.childrenExpanded ? jsx(ChevronDownIcon, {}) : jsx(ChevronRightIcon, {})
    }) : jsx("span", {
      className: "cogs-datagrid-button-spacer"
    }), jsx(Tooltip, {
      content: value,
      disabled: !isOverflowing,
      children: jsxs("div", {
        ref: ref,
        className: "cogs-lab cogs-datagrid-tree-cell",
        children: [value, "\xA0", filteredDescendantCount > 0 && "(".concat(filteredDescendantCount, ")")]
      })
    })]
  }, id);
};

export { DataGridAvatarCell, DataGridChipCell, DataGridColumnFilteredIcon, DataGridColumnMenu, DataGridColumnReorderIcon, DataGridColumnSortedAscendingIcon, DataGridColumnSortedDescendingIcon, DataGridEditableCell, DataGridHeaderCell, DataGridLinkCell, DataGridNoResult, DataGridNoRows, DataGridNumberCell, DataGridRow, DataGridRowStringCell, DataGridToolbar, DataGridTreeCell };
